import {
  type EvmAddressSummaryInfo,
  type EvmItemActivityInfo,
  type EvmItemActivityListInfo,
  type EvmNftActivityInfo,
  type EvmNftActivityListInfo,
  type EvmNftCardInfo,
  type EvmNftMintInfo,
  type EvmNftMintListInfo,
  type EvmNftTransferInfo,
  type EvmNftTransferListInfo,
  type EvmTokenItemHolderInfo,
  type EvmTokenItemHolderListInfo,
  type EvmTokenItemProfileInfo,
  type EvmTokenOwnerHistoryInfo,
  type EvmTokenOwnerHistoryListInfo,
  type EvmTokenSummaryInfo,
  type EvmTokenTransferInfo,
  type EvmTokenTransferListInfo,
  type NexonTrxNftTransferInfo,
} from '~/api/repository'
import { AddressSummaryModel } from '~/types/model/address.model'
import { ListBaseModel } from '~/types/model/base.model'
import {
  TokenCardModel,
  TokenHolderBaseModel, TokenSummaryModel,
  TokenTransferModel,
} from '~/types/model/token.model'
import { INftType } from '~/types/token-type.type'
import { secondsToDate } from '~/utils/dateFormat'
import { setComma } from '~/utils/numberFormat'

/** @description
 * erc721, erc1155 트랜스퍼 테이블 row 모델
 */
export class NftTransferModel extends TokenTransferModel {
  protected _tokenId: string
  protected _tokenName?: string
  protected _tokenImage?: string
  protected _nftType?: string

  constructor(data: EvmTokenTransferInfo | EvmNftTransferInfo | NexonTrxNftTransferInfo) {
    super(data as EvmTokenTransferInfo | EvmNftTransferInfo)
    this._tokenId = data.TKNID
    if ('TKNNM' in data) {
      this._tokenName = data.TKNNM
    }
    if ('IMGURL' in data) {
      this._tokenImage = data.IMGURL
    }
    if ('TKNIMG' in data) {
      this._tokenImage = data.TKNIMG
    }
    if ('TP' in data) {
      this._nftType = data.TP
    }
  }

  get tokenId() {
    return this._tokenId
  }

  get item() {
    return this._tokenName || '-'
  }

  get nftType() {
    return this._nftType as INftType
  }

  get tokenImage() {
    return this._tokenImage || ''
  }

  get collection() {
    return this.tokenInfo.name || this.tokenInfo.symbol || '-'
  }
}

/** @description
 * erc721 + erc1155 트랜스퍼 테이블 모델
 * 기존 NftTransferModel에 nft의 타입 필드 추가
 */
export class NftAllTransferModel extends NftTransferModel {
  private _nftType: string

  constructor(data: EvmNftTransferInfo) {
    super(data)
    this._nftType = data.TP
  }

  get nftType() {
    return this._nftType as INftType
  }
}

/** @description
 * erc721, erc1155 트랜스퍼 리스트 모델
 */
export class NftTransferListModel extends ListBaseModel {
  private _transferList: Array<EvmTokenTransferInfo>

  constructor(data: EvmTokenTransferListInfo) {
    super(data.TCNT, data.VCNT)
    this._transferList = data.TKNTFLIST
  }

  get transferList(): Array<NftTransferModel> {
    return this._transferList.map(transfer => new NftTransferModel(transfer))
  }
}

/** @description
 * erc721 + erc1155 트랜스퍼 리스트 모델
 */
export class NftAllTransferListModel extends ListBaseModel {
  private _transferList: Array<EvmNftTransferInfo>

  constructor(data: EvmNftTransferListInfo) {
    super(data.TCNT, data.VCNT)
    this._transferList = data.NFTLIST
  }

  get transferList(): Array<NftAllTransferModel> {
    return this._transferList.map(transfer => new NftAllTransferModel(transfer))
  }
}

/** @description
 * erc721, erc1155 상세 홀더 테이블 row 모델
 */
export class NftItemHolderModel extends TokenHolderBaseModel {
  private _transactionHash: string
  private _age: string
  private _unixTimeStamp: number

  constructor(data: EvmTokenItemHolderInfo) {
    super(data)
    this._transactionHash = data.TRXHA
    this._age = data.AG
    this._unixTimeStamp = data.UT
  }

  get transactionHash(): string {
    return this._transactionHash
  }

  get age(): string {
    return this._age
  }

  get unixTimeStamp(): string {
    return secondsToDate(this._unixTimeStamp)
  }
}

export class NftItemHolderListModel extends ListBaseModel {
  private _totalOwnerCount: string
  private _holderList: Array<EvmTokenItemHolderInfo>

  constructor(data: EvmTokenItemHolderListInfo | EvmTokenItemHolderInfo) {
    if ('TKNITHDRLIST' in data) { // erc1155의 경우 리스트 응답이므로 그대로
      super(data.TCNT, data.VCNT)
      this._totalOwnerCount = data.TOWNCNT
      this._holderList = data.TKNITHDRLIST
    } else { // erc721의 경우 단일 응답이므로 리스트화
      super('1', '1')
      this._totalOwnerCount = '1'
      this._holderList = [ data ]
    }
  }

  get totalOwnerCount(): string {
    return setComma(this._totalOwnerCount)
  }

  get holderList() {
    return this._holderList.map(holder => new NftItemHolderModel(holder))
  }
}

export class NftItemProfileModel {
  readonly tokenId: string
  readonly image: string
  readonly _creator: EvmAddressSummaryInfo
  readonly _owner: string
  readonly _lastTraded: string
  readonly _tokenType: string
  readonly _tokenContract: EvmAddressSummaryInfo
  readonly tokenName: string
  readonly tokenImage: string
  readonly tokenDescription: string

  constructor(data: EvmTokenItemProfileInfo) {
    this.tokenId = data.TKNID
    this._tokenType = data.TKNST
    // this._totalSupply = data.QTY
    this._lastTraded = data.LT
    this.image = data.IMG
    this._tokenContract = data.TKNCTR
    this._owner = data.OWN
    this._creator = data.CRT
    this.tokenName = data.TKNM
    this.tokenImage = data.TKIMG
    this.tokenDescription = data.TKDESC || '-'
  }

  get tokenType() {
    return this._tokenType as INftType
  }

  get tokenContract(): AddressSummaryModel {
    return new AddressSummaryModel(this._tokenContract)
  }

  get creator(): AddressSummaryModel {
    return new AddressSummaryModel(this._creator)
  }

  get owner(): string {
    if (this.tokenType === INftType.ERC1155) {
      return setComma(this._owner)
    }
    return this._owner
  }

  get lastTraded(): string {
    if (!Number(this._lastTraded)) {
      return ''
    }
    return setComma(this._lastTraded)
  }
}

export class NftItemActivityModel {
  private _transactionHash: string
  private _method: string
  private _amount: string
  private _fromAddressInfo: EvmAddressSummaryInfo
  private _toAddressInfo: EvmAddressSummaryInfo
  private _age: string
  private _unixTimeStamp: number

  constructor(data: EvmItemActivityInfo) {
    this._transactionHash = data.TRXHA
    this._method = data.MTH
    this._amount = data.QTY
    this._fromAddressInfo = data.ADDRSFROMINFO
    this._toAddressInfo = data.ADDRSTOINFO
    this._age = data.AG
    this._unixTimeStamp = data.UT
  }

  get transactionHash(): string {
    return this._transactionHash
  }

  get method(): string {
    return this._method
  }

  get amount(): string {
    return setComma(this._amount)
  }

  get fromAddressInfo(): AddressSummaryModel {
    return new AddressSummaryModel(this._fromAddressInfo)
  }

  get toAddressInfo(): AddressSummaryModel {
    return new AddressSummaryModel(this._toAddressInfo)
  }

  get age(): string {
    return this._age
  }

  get unixTimeStamp(): string {
    return secondsToDate(this._unixTimeStamp)
  }
}

export class NftItemActivityListModel extends ListBaseModel {
  private _activityList: Array<EvmItemActivityInfo>

  constructor(data: EvmItemActivityListInfo) {
    super(data.TCNT, data.VCNT)
    this._activityList = data.ITACTLIST
  }

  get activityList(): Array<NftItemActivityModel> {
    return this._activityList.map(activity => new NftItemActivityModel(activity))
  }
}

export class NftItemHolderHistoryModel {
  private _method: string
  private _fromAddressInfo: EvmAddressSummaryInfo
  private _toAddressInfo: EvmAddressSummaryInfo
  private _amount: string
  private _transactionHash: string
  private _age: string
  private _unixTimeStamp: number

  constructor(data: EvmTokenOwnerHistoryInfo) {
    this._method = data.EVT
    this._fromAddressInfo = data.ADDRSFROMINFO
    this._toAddressInfo = data.ADDRSTOINFO
    this._amount = data.QTY
    this._transactionHash = data.TRXHA
    this._age = data.AG
    this._unixTimeStamp = data.UT
  }

  get method(): string {
    return this._method
  }

  get fromAddressInfo(): AddressSummaryModel {
    return new AddressSummaryModel(this._fromAddressInfo)
  }

  get toAddressInfo(): AddressSummaryModel {
    return new AddressSummaryModel(this._toAddressInfo)
  }

  get amount(): string {
    return setComma(this._amount)
  }

  get transactionHash(): string {
    return this._transactionHash
  }

  get age(): string {
    return this._age
  }

  get unixTimeStamp(): string {
    return secondsToDate(this._unixTimeStamp)
  }
}

export class NftItemHolderHistoryListModel extends ListBaseModel {
  private _holderHistoryList: Array<EvmTokenOwnerHistoryInfo>

  constructor(data: EvmTokenOwnerHistoryListInfo) {
    super(data.TCNT, data.VCNT)
    this._holderHistoryList = data.TKNOWNHISLIST
  }

  get holderHistoryList(): Array<NftItemHolderHistoryModel> {
    return this._holderHistoryList.map(holderHistory => new NftItemHolderHistoryModel(holderHistory))
  }
}

export class NftCardModel extends TokenCardModel {
  readonly _volume: string
  readonly _volumeChange: string
  readonly _minPrice: string

  constructor(data: EvmNftCardInfo) {
    super(data)
    this._volume = data.VOL
    this._volumeChange = data.VOL24H
    this._minPrice = data.MINPRC
  }

  get volume(): string {
    return setComma(this._volume)
  }

  get volumeChange(): string {
    return setComma(setDecimal(this._volumeChange, 2))
  }

  get minPrice(): string {
    return setComma(this._minPrice)
  }
}

export class NftCardListModel extends ListBaseModel {
  private _nftCardList: Array<EvmNftCardInfo>

  constructor(data: EvmNftCardListInfo) {
    super(data.TCNT)
    this._nftCardList = data.CARDLIST
  }

  get nftCardList(): Array<NftCardModel> {
    return this._nftCardList.map((nftCard) => new NftCardModel(nftCard))
  }
}

export class NftTradeModel {
  private _transactionHash: string
  private _age: string
  private _unixTimeStamp: number
  private _sellerAddressInfo: EvmAddressSummaryInfo
  private _buyerAddressInfo: EvmAddressSummaryInfo
  private _price: string
  private _quantity: string
  private _tokenInfo: EvmTokenSummaryInfo
  private _tokenId: string
  private _tokenName: string
  private _tokenImage: string
  private _nftType: string

  constructor(data: EvmNftActivityInfo) {
    this._transactionHash = data.TRXHA
    this._age = data.AG
    this._unixTimeStamp = data.UT
    this._sellerAddressInfo = data.ADDRSFROMINFO
    this._buyerAddressInfo = data.ADDRSTOINFO
    this._price = data.PRC
    this._nftType = data.TP
    this._quantity = data.VAL
    this._tokenInfo = data.TKNCTR
    this._tokenId = data.TKNID
    this._tokenName = data.TKNNM
    this._tokenImage = data.IMGURL
  }

  get transactionHash() {
    return this._transactionHash
  }

  get age() {
    return this._age
  }

  get unixTimeStamp() {
    return secondsToDate(this._unixTimeStamp)
  }

  get sellerAddressInfo() {
    return new AddressSummaryModel(this._sellerAddressInfo)
  }

  get buyerAddressInfo() {
    return new AddressSummaryModel(this._buyerAddressInfo)
  }

  get price() {
    return setComma(this._price)
  }

  get nftType() {
    return this._nftType as INftType
  }

  get quantity() {
    return setComma(this._quantity)
  }

  get tokenInfo() {
    return new TokenSummaryModel(this._tokenInfo)
  }

  get tokenId() {
    return this._tokenId
  }

  get tokenName() {
    return this._tokenName
  }

  get tokenImage() {
    return this._tokenImage
  }
}

export class NftTradeListModel extends ListBaseModel {
  private _tradeList: Array<EvmNftActivityInfo>

  constructor(data: EvmNftActivityListInfo) {
    super(data.TCNT, data.VCNT)
    this._tradeList = data.NFTLIST
  }

  get tradeList() {
    return this._tradeList.map(trade => new NftTradeModel(trade))
  }
}

export class NftMintModel {
  private _transactionHash: string
  private _age: string
  private _unixTimeStamp: number
  private _minterAddressInfo: EvmAddressSummaryInfo
  private _quantity: string
  private _tokenInfo: EvmTokenSummaryInfo
  private _tokenId: string
  private _tokenName: string
  private _tokenImage: string
  private _nftType: string

  constructor(data: EvmNftMintInfo) {
    this._transactionHash = data.TRXHA
    this._age = data.AG
    this._unixTimeStamp = data.UT
    this._minterAddressInfo = data.ADDRSTOINFO
    this._quantity = data.VAL
    this._tokenInfo = data.TKNCTR
    this._tokenId = data.TKNID
    this._tokenName = data.TKNNM
    this._tokenImage = data.IMGURL
    this._nftType = data.TP
  }

  get transactionHash() {
    return this._transactionHash
  }

  get age() {
    return this._age
  }

  get unixTimeStamp() {
    return secondsToDate(this._unixTimeStamp)
  }

  get minterAddressInfo() {
    return new AddressSummaryModel(this._minterAddressInfo)
  }

  get quantity() {
    return setComma(this._quantity)
  }

  get tokenInfo() {
    return new TokenSummaryModel(this._tokenInfo)
  }

  get tokenId() {
    return this._tokenId
  }

  get nftType() {
    return this._nftType as INftType
  }

  get tokenName() {
    return this._tokenName
  }

  get tokenImage() {
    return this._tokenImage
  }
}

export class NftMintListModel extends ListBaseModel {
  private _mintList: Array<EvmNftMintInfo>

  constructor(data: EvmNftMintListInfo) {
    super(data.TCNT, data.VCNT)
    this._mintList = data.NFTLIST
  }

  get mintList() {
    return this._mintList.map(mint => new NftMintModel(mint))
  }
}


export class NexonTrxNftTransferInfoModel {
  
}